<template>
<div class="comment-container" :class="{
      'posted': posted,
      'highlighted': highlighted,
      'won-contest': commentCompetitionEnabled && comment.lang === $i18n.locale,
      'has-replies': comment.replies
    }">
  <div class="comment">
    <div class="w-100">
      <div class="comment--header">
        <div class="data d-flex flex-wrap">
          <h3 class="comment--header__author mb-0">
            <span v-if="comment.avatar" class="avatar">
              <img :src="comment.avatar" />
            </span>
            <span v-if="comment.author_name === 'Unexisting user'" class="font-italic semi-bold">
              {{$t('auth.deletedUser')}}
            </span>
            <span v-else>
              {{comment.author_name}}
            </span>
          </h3>
          <p class="comment--header__date mb-0">{{comment.date | moment("DD.MM.YYYY, H:mm")}}</p>
        </div>
        <div v-if="highlighted" class="comment--highlights">
          <div v-if="commentCompetitionEnabled && comment.lang === $i18n.locale"
              class="picked-winner">
            <img class="img-fluid icon-trophy filter-to-white"
                  src="@/assets/icons/trophy.svg"/>
            <p class="text-xs m-0"><span class="bold">{{$t('comments.staffPicked')}}</span> &amp; {{$t('comments.winner')}}</p>
          </div>
          <div v-else class="picked">
            <p class="text-xs m-0"><span class="bold">{{$t('comments.staffPicked')}}</span></p>
          </div>
        </div>
      </div>
      <div class="comment--body">
        <div v-if="comment.lang && (comment.lang !== $i18n.locale)">
          <CommentContentWithTranslation :content="comment.content.raw"
                                         :source="comment.lang"
                                         :target="$i18n.locale"/>
        </div>
        <div v-else v-html="comment.content.raw ? comment.content.raw : comment.content.rendered"></div>
        <div v-if="comment.attachments" class="comment-attachments mt-3">
          <div v-for="(attachment, i) in comment.attachments" :key="i"
               @click="openLightbox(i)"
               class="comment-attachments--item pointer mr-3 mb-3" >
            <div v-if="attachment.image">
              <img :src="attachment.image.sizes.thumbnail" class="img-fluid">
            </div>
          </div>
          <CoolLightBox
            :items="lightboxItems"
            :index="lightboxIndex"
            :slideshow="false"
            :thumbsPosition="'bottom'"
            @close="lightboxIndex = null">
          </CoolLightBox>
        </div>
      </div>
      <div class="comment--footer">
        <div class="comment--footer__actions">
          <div class="like text-secondary">
            <span class="mr-1 text-xs cursor-default bold">{{comment.likes_count}}</span>
            <span v-if="!loggedIn"
                  :id="`popover-not-loggedin-${comment.id}`">
              <font-awesome-icon :icon="['far', 'thumbs-up']"/>
              <b-popover
                v-if="!loggedIn"
                :target="`popover-not-loggedin-${comment.id}`"
                :placement="'top'"
                triggers="hover focus"
              >
                <div class="popover-not-loggedin-content">
                  <h5 class="text-sm">{{$t('auth.youreNotLoggedIn')}}</h5>
                  <p class="text-sm font-weight-light m-0">
                    <localized-link :to="{name: 'Login'}">
                      <span class="text-secondary">{{$t('auth.login')}}</span>
                    </localized-link> {{$t('comments.or')}}
                    <localized-link :to="{name: 'Signup'}"><span class="text-secondary">{{$t('auth.register')}}</span></localized-link>
                  </p>
                </div>
              </b-popover>
            </span>
            <span v-else>
              <font-awesome-icon v-if="comment.liked"
                                 @click="!sending ? dislikeComment(comment.id) : false"
                                 :class="{'pointer': !sending}"
                                 :icon="['fas', 'thumbs-up']"/>
              <font-awesome-icon v-else
                                 @click="!sending ? likeComment(comment.id) : false"
                                 :class="{'pointer': loggedIn && !sending}"
                                 :icon="['far', 'thumbs-up']"/>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="comment.replies" class="comment-replies">
    <div v-for="reply in comment.replies" :key="reply.id" class="reply-wrapper">
      <div class="reply">
        <div class="data d-flex flex-wrap">
          <h3 class="comment--header__author mb-0">
            {{$t('comments.answeredBy')}} <span class="bold">{{$t('comments.editorialStaff')}}</span>
          </h3>
          <p class="comment--header__date mb-0">{{reply.date | moment("DD.MM.YYYY, H:mm")}}</p>
        </div>
        <div class="comment--body">
          <div v-if="comment.lang && (comment.lang !== $i18n.locale)">
            <CommentContentWithTranslation :content="reply.content.raw"
                                           :target="$i18n.locale"/>
          </div>
          <div v-else v-html="reply.content.raw ? reply.content.raw : reply.content.rendered"></div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>
<script>
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';
import CommentContentWithTranslation from './CommentContentWithTranslation.vue';

export default {
  components: {
    CommentContentWithTranslation,
    CoolLightBox,
  },
  props: {
    comment: null,
    posted: Boolean,
    highlighted: {
      type: Boolean,
      default: false,
    },
    commentCompetitionEnabled: Boolean,
  },
  data() {
    return {
      sending: false,
      lightboxItems: [],
      lightboxIndex: null,
    };
  },
  computed: {
    loggedIn() {
      return this.$store.state.user.status.loggedIn;
    },
  },
  methods: {
    likeComment(commentId) {
      this.sending = true;
      this.$store.dispatch('comment/LIKE_COMMENT', commentId).then(() => {
        this.sending = false;
      }).catch((error) => {
        this.showErrorMessage(error);
        this.sending = false;
      });
    },
    dislikeComment(commentId) {
      this.sending = true;
      this.$store.dispatch('comment/DISLIKE_COMMENT', commentId).then(() => {
        this.sending = false;
      }).catch((error) => {
        this.showErrorMessage(error);
        this.sending = false;
      });
    },
    showErrorMessage(message) {
      this.$bvToast.toast('Error', {
        title: message,
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000,
        toaster: 'b-toaster-bottom-center',
        bodyClass: 'd-none',
      });
    },
    openLightbox(index) {
      const gallery = this.comment.attachments.map((pic) => {
        return { src: pic.image.sizes['2048x2048'] };
      });
      this.lightboxItems = gallery;
      this.lightboxIndex = index;
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/scss/common.scss';
.comment-container {
  &.posted .comment {
    animation: border-out 8s linear 1;
  }
  &.won-contest {
    .comment, .reply-wrapper {
      background-color: $light-tertiary!important;
      border-color: $tertiary!important;
    }
  }
  &.has-replies {
    .comment {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom: 0!important;
    }
  }
  &.highlighted {
    .comment, .reply-wrapper {
      background-color: lighten($secondary, 40%);
    }
  }
  &.highlighted {
    .comment, .reply-wrapper {
      background-color: lighten($secondary, 40%);
      border-color: $secondary;
      .comment--highlights {
        .picked,
        .picked-winner {
          padding: 5px 25px;
          height: 43px;
          display: flex;
          align-items: center;
          p {
            color: white;
            @extend .font-italic-custom;
          }
        }
        .picked {
          background-color: $secondary;
        }
        .picked-winner {
          display: flex;
          align-items: center;
          background-color: $tertiary;
          .icon-trophy {
            max-width: 30px;
            width: 100%;
            margin-right: 10px;
          }
        }
      }
      .comment--body {
        .translation-info-link {
          color: lighten($primary, 30%);
          text-decoration: underline;
        }
      }
    }
  }
}
.comment, .reply-wrapper {
  background-color: $light-grey;
  .translation-info {
    color: #a1a1a1;
    &-link {
      color: lighten($secondary, 10%);;
    }
  }
}
.reply-wrapper {
  border-left: 1px solid;
  border-right: 1px solid;
  border-color:  $light-grey;
  border-top: 0;
  border-bottom: 0;
  margin-top: 3px;
  padding: 1rem 2rem 1rem 50px;
  &:last-of-type {
    border-bottom: 1px solid;
    border-color: $light-grey;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
}
.reply {
  padding-left: 1.5rem;
  border-left: 1px solid #b5b5b5;
  .comment--header {
    &__author {
      font-weight: 300;
      color: $secondary;
    }
    &__date {
      color: $secondary;
    }
  }
}
.comment {
  padding: 0 2rem 2rem 50px;
  border-radius: 8px;
  border: 1px solid transparent;
  position: relative;
  &--header {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap-reverse;
    width: 100%;
    margin-bottom: 1rem;
    .data {
      padding-top: 1.5rem;
    }
    &__author {
      @extend .text-sm;
      margin-right: 15px;
      font-weight: bold;
      .avatar {
        margin-right: 5px;
        img {
          max-width: 30px;
          border-radius: 50%;
        }
      }
    }
    &__date {
      @extend .text-sm;
    }
    @include media-breakpoint-down(sm) {
      flex-direction: column-reverse;
      align-content: flex-start;
      .data {
        width: 100%;
      }
      .comment--highlights {
        width: fit-content;
        .picked,
        .picked-winner {
          height: unset!important;
        }
        .picked-winner {
          img {
            max-width: 25px!important;
          }
        }
      }
    }
  }
  &--body {
    @extend .text-sm;
    color: #424242;
    p {
      margin: 0;
    }
  }
  &--footer {
    &__actions {
      display: flex;
      justify-content: flex-end;
    }
  }
}
.comment-attachments {
  display: flex;
  &--item {
    img {
      width: 100px;
    }
  }
  @include media-breakpoint-down(md) {
    &--item img {
      width: 80px;
    }
  }
  @include media-breakpoint-down(sm) {
    flex-wrap: wrap;
    &--item img {
      width: 60px;
    }
  }
}
@keyframes border-out {
  from {
    border-color: $secondary;
  }
  to {
    border-color: transparent;
  }
}
</style>
